import {
  Button,
  ConfirmationDialog,
  GlobalConfirmationDialog,
} from 'src/design-system'
import { flexRender } from '@tanstack/react-table'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { useCheckinsTable } from './context/checkins-table'
import * as Table from 'components/table'
import { Filters } from './filters'
import { store } from 'store/index'
import { BulkCheckinSelectionContext } from '../bulk-selection-context'
import { successToast } from 'app/packs/src/utils/success-toast'
import { errorToast } from 'app/packs/src/utils/error-toast'

export const CheckinsTable = observer(() => {
  const { data, table, loading } = useCheckinsTable()
  const {
    checkedCheckinIds,
    addCheckedCheckinId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeCheckedCheckinId,
    clearCheckedCheckinIds,
  } = React.useContext(BulkCheckinSelectionContext)

  const source = 'org-checkin-insights-table'
  const isAdmin = !!store.currentUser?.isAdmin
  const selectedCount = checkedCheckinIds.length
  const isSnackbarVisible = selectedCount > 0

  React.useEffect(() => {
    table.resetColumnFilters()

    const search = new URLSearchParams(window.location.search)
    const userFilter = search.get('user_id')
    if (userFilter) {
      table.setColumnFilters([{ id: 'name', value: userFilter }])
    }
  }, [table])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearCheckedCheckinIds()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [clearCheckedCheckinIds])

  const percentageComplete =
    100 *
    (data.completedCheckinCount(table.getRowModel()) /
      data.totalCheckinCount(table.getRowModel()))

  function getNum(val: number) {
    if (isNaN(val)) {
      return 0
    }
    return val
  }

  const handleBulkSelectToggle = () => {
    const allCurrentPageIds = table
      .getRowModel()
      .rows.filter((row) => !!row.original.id)
      .map((row) => row.original.id as string)

    const allSelected = allCurrentPageIds.every((id) =>
      checkedCheckinIds.includes(id)
    )

    if (allSelected) {
      clearCheckedCheckinIds()
    } else {
      allCurrentPageIds.forEach((id) => {
        if (!checkedCheckinIds.includes(id)) {
          addCheckedCheckinId(id)
        }
      })
    }
  }

  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const handleBulkArchive = React.useCallback(async () => {
    const count = checkedCheckinIds.length
    const result = await store.checkins.bulkArchive(checkedCheckinIds)
    if (result.success) {
      successToast(`${count} check-in(s) archived!`)
      clearCheckedCheckinIds()
    } else {
      errorToast('Failed to archive check-in(s).')
    }
  }, [checkedCheckinIds, clearCheckedCheckinIds])

  const handleBulkDelete = React.useCallback(async () => {
    const count = checkedCheckinIds.length
    const result = await store.checkins.bulkDestroy(checkedCheckinIds)
    if (result.success) {
      successToast(`${count} check-in(s) deleted!`)
      clearCheckedCheckinIds()
    } else {
      errorToast('Failed to delete check-in(s).')
    }
  }, [checkedCheckinIds, clearCheckedCheckinIds])

  return (
    <div>
      <div className="mb-4 lg:mb-6">
        <Filters />
        {store.nonNullCurrentUser.isAdmin && (
          <div className="px-4">
            <h3 className="text-base font-bold">
              {data.completedCheckinCount(table.getRowModel())} of{' '}
              {data.totalCheckinCount(table.getRowModel())} Check-ins completed
              ({getNum(percentageComplete).toFixed(0)}%)
            </h3>
          </div>
        )}
      </div>
      <div className="overflow-x-auto pb-10">
        <Table.Root>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <Table.Header
                    key={header.id}
                    header={header}
                    source={source}
                    loading={loading}
                  >
                    {index === 0 ? (
                      <div className="flex gap-x-2 pl-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={
                            table.getRowModel().rows.length > 0 &&
                            table
                              .getRowModel()
                              .rows.filter((row) => !!row.original.id)
                              .every((row) =>
                                checkedCheckinIds.includes(
                                  row.original.id as string
                                )
                              )
                          }
                          onChange={handleBulkSelectToggle}
                        />
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )
                    )}
                  </Table.Header>
                ))}
              </Table.Row>
            ))}
          </Table.Head>

          <Table.Body>
            {table.getRowModel().rows.length === 0 && (
              <Table.NoResultsRow colspan={table.getAllColumns().length} />
            )}

            {table.getRowModel().rows.map((row, index) => {
              const cells = row.getVisibleCells()

              return (
                <Table.Row
                  className={
                    row.original.id &&
                    checkedCheckinIds.includes(row.original.id)
                      ? 'bg-gray-50/50'
                      : ''
                  }
                  showBottomBorder={index < table.getRowModel().rows.length - 1}
                  key={row.id}
                >
                  {cells.map((cell) => (
                    <Table.Cell key={cell.id} className="whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
        {isSnackbarVisible && (
          <div className="fixed bottom-6 left-[60%] px-6 py-3 translate-x-[-50%]">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="flex items-center gap-4 px-6 py-3">
                <span className="text-xs leading-5 text-[#1B1818]">
                  {selectedCount} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="text-sm px-3"
                    variant="outline"
                    onClick={() => setArchiveDialogOpen(true)}
                  >
                    Archive
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      colourVariant="danger"
                      className="text-sm !text-[#E42309] !border-[#E42309] px-3 items-center gap-x-1.5"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0"
                      >
                        <path
                          d="M9.50098 0.5C9.96493 0.500129 10.4102 0.684622 10.7383 1.0127C11.0663 1.34086 11.251 1.78599 11.251 2.25V3H13.5C13.9142 3 14.25 3.33579 14.25 3.75C14.25 4.16421 13.9142 4.5 13.5 4.5H13.251V13C13.251 13.3314 13.119 13.6494 12.8848 13.8838C12.6505 14.1181 12.3323 14.2499 12.001 14.25H4.00098C3.66946 14.25 3.35063 14.1182 3.11621 13.8838C2.88194 13.6494 2.75098 13.3314 2.75098 13V4.5H2.5C2.0859 4.49987 1.75 4.16413 1.75 3.75C1.75 3.33587 2.0859 3.00013 2.5 3H4.75098V2.25C4.75098 1.78599 4.93466 1.34086 5.2627 1.0127C5.59088 0.684507 6.03685 0.5 6.50098 0.5H9.50098ZM4.25098 12.75H11.751V4.5H4.25098V12.75ZM6.50098 5.75C6.91497 5.75026 7.25098 6.08595 7.25098 6.5V10.5C7.25098 10.9141 6.91497 11.2497 6.50098 11.25C6.08676 11.25 5.75098 10.9142 5.75098 10.5V6.5C5.75098 6.08579 6.08676 5.75 6.50098 5.75ZM9.50098 5.75C9.91497 5.75026 10.251 6.08595 10.251 6.5V10.5C10.251 10.9141 9.91497 11.2497 9.50098 11.25C9.08676 11.25 8.75098 10.9142 8.75098 10.5V6.5C8.75098 6.08579 9.08676 5.75 9.50098 5.75ZM6.50098 2C6.43467 2 6.37013 2.02636 6.32324 2.07324C6.27651 2.1201 6.25098 2.18381 6.25098 2.25V3H9.75098V2.25C9.75098 2.18381 9.72447 2.1201 9.67773 2.07324C9.63097 2.02647 9.5671 2.00013 9.50098 2H6.50098Z"
                          fill="#E42309"
                        />
                      </svg>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <ConfirmationDialog.Root
          open={archiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          title={`Are you sure you want to archive ${selectedCount} check-in${
            selectedCount > 1 ? 's' : ''
          }?`}
          body="Archived check-ins will be hidden from the check-ins list."
          onConfirm={handleBulkArchive}
          confirmLabel={`Archive ${selectedCount} check-in${
            selectedCount > 1 ? 's' : ''
          }`}
        />
        <ConfirmationDialog.Root
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Are you sure you want to delete this user's check-in data?"
          body="Once the data is deleted, you may not undo."
          onConfirm={handleBulkDelete}
          confirmLabel="Delete"
          confirmColourVariant="danger"
          cancelColourVariant="theme"
          reverseButtons
        />
      </div>
      <GlobalConfirmationDialog id={`assessor-select`} />
    </div>
  )
})
